const marker = 'mentiwayModal';
type Entry = { id: string; url: string; state: any; close: () => void };
const entries = new Map<string, Entry>();
const retiredEntries = new Map<string, Entry>();
const listenerOwner = window as Window & { mentiwayModalPopListener?: (event: PopStateEvent) => void };
let sequence = 0;
const instanceId = Math.random().toString(36).slice(2);
let current: Entry | undefined;
let listening = false;

const samePage = (entry: Entry, state: any) =>
	window.location.pathname === new URL(entry.url).pathname && state?.key === entry.state?.key;

const push = (entry: Entry) => {
	window.history.pushState({ ...entry.state, [marker]: entry.id }, '', entry.url);
	current = entry;
};

const handlePop = (event: PopStateEvent) => {
	const previous = current;
	const target = entries.get(event.state?.[marker]);
	current = target;
	if (previous && samePage(previous, event.state)) {
		// The browser consumed a dialog entry, not a route. Keep the router on the page.
		event.stopImmediatePropagation();
		if (entries.has(previous.id)) {
			// Back already consumed the dialog entry. Do not push it again and then
			// issue another Back from cleanup: Chrome can skip real pages in that flow.
			previous.close();
			const retired = retiredEntries.get(event.state?.[marker]);
			if (retired && samePage(retired, event.state)) window.history.back();
			setTimeout(() => {
				// Restore the entry only when onHide refused to close an unsaved draft.
				if (entries.has(previous.id) && current === target && samePage(previous, window.history.state)) {
					push(previous);
				}
			}, 0);
			return;
		}
		const retired = retiredEntries.get(event.state?.[marker]);
		if (retired && samePage(retired, event.state)) window.history.back();
		return;
	}
	if (event.state?.[marker] && !target) {
		const retired = retiredEntries.get(event.state[marker]);
		if (retired && samePage(retired, event.state)) {
			// Only skip entries created and retired by this manager. An unknown marker
			// can survive a reload and must never cause another history traversal.
			event.stopImmediatePropagation();
			window.history.back();
		} else {
			const state = { ...event.state };
			delete state[marker];
			window.history.replaceState(state, '', window.location.href);
		}
	}
};

export const registerModalHistory = (close: () => void) => {
	if (!listening) {
		// Fast Refresh must not leave multiple managers processing the same Back.
		if (listenerOwner.mentiwayModalPopListener) {
			window.removeEventListener('popstate', listenerOwner.mentiwayModalPopListener, true);
		}
		listenerOwner.mentiwayModalPopListener = handlePop;
		window.addEventListener('popstate', handlePop, true);
		listening = true;
	}
	sequence += 1;
	const entry: Entry = {
		id: `modal-${instanceId}-${sequence}`,
		url: window.location.href,
		state: window.history.state,
		close,
	};
	entries.set(entry.id, entry);
	push(entry);
	return () => {
		entries.delete(entry.id);
		retiredEntries.set(entry.id, entry);
		// Let the React commit (including unmount cleanup) finish first. A dialog
		// opened by the same action must not be consumed by this asynchronous Back.
		setTimeout(() => {
			if (window.history.state?.[marker] === entry.id && samePage(entry, window.history.state)) {
				window.history.back();
			}
		}, 0);
	};
};
